import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, ReceiptText, FileX, CircleX, PencilLine } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiCallError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/components/ui/empty';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { DatePicker } from '@/components/ui/date-picker';
import { formatMoney } from '@/components/app-document-detail';

interface Comprobante {
  id: string;
  direccion: 'compra' | 'venta';
  numeroComprobante: string;
  timbrado: string | null;
  fechaEmision: string | null;
  condicionVenta: string | null;
  estado: string | null;
  contraparteRuc: string | null;
  contraparteNombre: string | null;
  gravada10: string;
  gravada5: string;
  exentas: string;
  iva10: string;
  iva5: string;
  ivaTotal: string;
  total: string;
  source: string;
}

const ERROR_LABEL: Record<string, string> = {
  no_credentials: 'No hay credenciales de Marangatu cargadas.',
  login_failed: 'No pudimos loguear (¿credenciales mal o cambió SET?).',
  login_offsite: 'El login redirigió fuera de SET — abortado.',
  comprobantes_url_not_found: 'No se encontró el menú de comprobantes en SET.',
  menu_consulta_not_found: 'No se encontró "Consulta de Comprobantes Registrados" en el menú de SET.',
  consulta_url_invalid: 'La URL de la consulta de SET no es válida.',
  consulta_tipo_compra_not_found: 'No encontré el selector "REGISTRO DE COMPRAS" en SET.',
  consulta_tipo_venta_not_found: 'No encontré el selector "REGISTRO DE VENTAS" en SET.',
  consulta_fecha_desde_not_found: 'No encontré el campo "Fecha de Emisión Desde" en SET.',
  consulta_fecha_hasta_not_found: 'No encontré el campo "Fecha de Emisión Hasta" en SET.',
  consulta_busqueda_not_found: 'No encontré el botón "Búsqueda" en SET.',
  nav_offsite: 'La navegación a SET redirigió fuera del portal.',
  sync_failed: 'Falló el scrape. Revisá las credenciales o probá de nuevo.',
};

const num = (s: string | null | undefined): number => Number(s ?? 0) || 0;

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
// Años seleccionables: año actual hacia atrás 5 (SET conserva históricos).
const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

/** dd/MM/yyyy desde un ISO date (yyyy-MM-dd). null-safe. */
function fmtFecha(iso: string | null): string {
  if (!iso) return '—';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

export function ComprobantesCard({ tenantId }: { tenantId: string }): React.ReactElement {
  const qc = useQueryClient();
  const base = `/api/admin/tenants/${tenantId}/set-access`;
  const [manualOpen, setManualOpen] = useState(false);
  // Período a sincronizar (SET pide Año+Mes en el paso Inicio). Default: mes actual.
  const now = new Date();
  const [syncYear, setSyncYear] = useState(String(now.getFullYear()));
  const [syncMonth, setSyncMonth] = useState(String(now.getMonth() + 1)); // 1-12
  const invalidate = () => qc.invalidateQueries({ queryKey: ['set-comprobantes', tenantId] });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['set-comprobantes', tenantId],
    queryFn: () => api<{ items: Comprobante[] }>(`${base}/comprobantes`),
  });
  const items = data?.items ?? [];

  const compras = useMemo(() => items.filter((c) => c.direccion === 'compra'), [items]);
  const ventas = useMemo(() => items.filter((c) => c.direccion === 'venta'), [items]);

  const sync = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; compras: number; ventas: number; ingresos?: number; error?: string }>(`${base}/sync-comprobantes`, {
        method: 'POST',
        body: JSON.stringify({ year: Number(syncYear), month: Number(syncMonth) }),
      }),
    onSuccess: (r) => {
      if (r.ok) {
        const nuevos = r.compras + r.ventas + (r.ingresos ?? 0);
        const partes = [
          r.compras > 0 ? `${r.compras} compras` : '',
          r.ventas > 0 ? `${r.ventas} ventas` : '',
          (r.ingresos ?? 0) > 0 ? `${r.ingresos} ingresos` : '',
        ].filter(Boolean).join(', ');
        toast.success(
          nuevos > 0
            ? `Se ingresaron ${nuevos} comprobante${nuevos === 1 ? '' : 's'} nuevo${nuevos === 1 ? '' : 's'}${partes ? ` (${partes})` : ''}.`
            : 'Sin comprobantes nuevos — ya estaba todo al día.'
        );
      } else {
        // Si el código no está mapeado, mostramos el crudo: los códigos
        // granulares del scraper (menu_*, siguiente_not_found, *_no_list_response,
        // *_over_200_use_file…) sirven para diagnosticar qué paso falló.
        toast.error(ERROR_LABEL[r.error ?? ''] ?? `No se pudo sincronizar — ${r.error ?? 'error'}`);
      }
      invalidate();
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiCallError
          ? `No se pudo sincronizar — ${e.body.error}`
          : 'No se pudieron sincronizar los comprobantes (¿timeout?)'
      ),
  });


  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ReceiptText className="size-4 text-muted-foreground" />
            Comprobantes electrónicos
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {/* Período a sincronizar (SET pide Año+Mes). */}
            <Select value={syncMonth} onValueChange={setSyncMonth} disabled={sync.isPending}>
              <SelectTrigger size="sm" className="w-[8.5rem]" aria-label="Mes">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MESES.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={syncYear} onValueChange={setSyncYear} disabled={sync.isPending}>
              <SelectTrigger size="sm" className="w-[5.5rem]" aria-label="Año">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" disabled={sync.isPending} onClick={() => sync.mutate()}>
              <RefreshCw className={sync.isPending ? 'animate-spin' : ''} />
              {sync.isPending ? 'Sincronizando…' : 'Sincronizar comprobantes'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setManualOpen(true)}>
              <PencilLine /> Cargar manual
            </Button>
          </div>
        </div>
        <CardDescription>
          Importá las e-facturas del contribuyente desde SET. Una vez ingestadas
          aparecen como facturas normales en la pestaña <strong>Facturas</strong> (no se listan acá).
        </CardDescription>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : isError ? (
          <p className="flex items-start gap-2 text-sm text-destructive">
            <CircleX className="mt-0.5 size-4 shrink-0" />
            No se pudieron cargar los comprobantes. Probá de nuevo.
          </p>
        ) : items.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileX />
              </EmptyMedia>
              <EmptyTitle>Sin comprobantes todavía</EmptyTitle>
              <EmptyDescription>
                Sincronizá para ingestar las e-facturas del contribuyente desde SET.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          // Resumen compacto: el detalle por comprobante vive en la pestaña Facturas
          // (los comprobantes de SET son facturas, evitamos duplicar la tabla acá).
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <div className="text-xs text-muted-foreground">Compras (IVA crédito)</div>
              <div className="text-xl font-bold tabular-nums">{compras.length}</div>
            </div>
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <div className="text-xs text-muted-foreground">Ventas (IVA débito)</div>
              <div className="text-xl font-bold tabular-nums">{ventas.length}</div>
            </div>
          </div>
        )}
      </CardContent>

      <ManualComprobanteDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        base={base}
        onSaved={() => {
          setManualOpen(false);
          invalidate();
        }}
      />
    </Card>
  );
}

interface ManualForm {
  direccion: 'compra' | 'venta';
  numeroComprobante: string;
  timbrado: string;
  fechaEmision: string;
  condicionVenta: 'contado' | 'credito' | '';
  contraparteRuc: string;
  contraparteNombre: string;
  gravada10: string;
  gravada5: string;
  exentas: string;
  iva10: string;
  iva5: string;
}

const EMPTY_FORM: ManualForm = {
  direccion: 'compra',
  numeroComprobante: '',
  timbrado: '',
  fechaEmision: '',
  condicionVenta: '',
  contraparteRuc: '',
  contraparteNombre: '',
  gravada10: '',
  gravada5: '',
  exentas: '',
  iva10: '',
  iva5: '',
};

const toNum = (s: string): number => Number(s.replace(/[^\d.-]/g, '')) || 0;

function ManualComprobanteDialog({
  open,
  onClose,
  base,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  base: string;
  onSaved: () => void;
}): React.ReactElement {
  const [form, setForm] = useState<ManualForm>(EMPTY_FORM);
  const set = <K extends keyof ManualForm>(k: K, v: ManualForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const ivaTotal = toNum(form.iva10) + toNum(form.iva5);
  const total = toNum(form.gravada10) + toNum(form.gravada5) + toNum(form.exentas) + ivaTotal;
  const contraparteLabel = form.direccion === 'compra' ? 'Proveedor' : 'Cliente';

  const save = useMutation({
    mutationFn: () =>
      api(`${base}/comprobantes`, {
        method: 'POST',
        body: JSON.stringify({
          direccion: form.direccion,
          numeroComprobante: form.numeroComprobante.trim(),
          timbrado: form.timbrado.trim() || null,
          fechaEmision: form.fechaEmision || null,
          condicionVenta: form.condicionVenta || null,
          contraparteRuc: form.contraparteRuc.trim() || null,
          contraparteNombre: form.contraparteNombre.trim() || null,
          gravada10: toNum(form.gravada10),
          gravada5: toNum(form.gravada5),
          exentas: toNum(form.exentas),
          iva10: toNum(form.iva10),
          iva5: toNum(form.iva5),
        }),
      }),
    onSuccess: () => {
      toast.success('Comprobante cargado');
      setForm(EMPTY_FORM);
      onSaved();
    },
    onError: (e) => {
      if (e instanceof ApiCallError && e.body.error === 'comprobante_already_exists') {
        toast.error('Ya existe un comprobante con ese número y timbrado.');
        return;
      }
      toast.error(e instanceof ApiCallError ? (e.body.message ?? e.body.error) : 'No se pudo guardar');
    },
  });

  const canSave = form.numeroComprobante.trim().length > 0 && !save.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[95vw] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cargar comprobante manualmente</DialogTitle>
          <DialogDescription>
            Fallback cuando el scrape no corre: copiá los datos del comprobante desde SET. El IVA total y
            el total se calculan solos desde las bases gravadas y el IVA.
          </DialogDescription>
        </DialogHeader>

        <FieldGroup className="max-h-[60vh] gap-4 overflow-y-auto py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="mc-dir">Dirección</FieldLabel>
              <Select value={form.direccion} onValueChange={(v) => set('direccion', v as 'compra' | 'venta')}>
                <SelectTrigger id="mc-dir">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compra">Compra (IVA crédito)</SelectItem>
                  <SelectItem value="venta">Venta (IVA débito)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="mc-cond">Condición de venta</FieldLabel>
              <Select
                value={form.condicionVenta || undefined}
                onValueChange={(v) => set('condicionVenta', v as 'contado' | 'credito')}
              >
                <SelectTrigger id="mc-cond">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contado">Contado</SelectItem>
                  <SelectItem value="credito">Crédito</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="mc-num">Número de comprobante</FieldLabel>
              <Input
                id="mc-num"
                value={form.numeroComprobante}
                onChange={(e) => set('numeroComprobante', e.target.value)}
                placeholder="001-001-0000123"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="mc-timb">Timbrado</FieldLabel>
              <Input
                id="mc-timb"
                value={form.timbrado}
                onChange={(e) => set('timbrado', e.target.value)}
                placeholder="12345678"
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="mc-fecha">Fecha de emisión</FieldLabel>
              <DatePicker
                id="mc-fecha"
                value={form.fechaEmision}
                onChange={(v) => set('fechaEmision', v)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="mc-cp-ruc">RUC del {contraparteLabel.toLowerCase()}</FieldLabel>
              <Input
                id="mc-cp-ruc"
                value={form.contraparteRuc}
                onChange={(e) => set('contraparteRuc', e.target.value)}
                placeholder="80012345-6"
              />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="mc-cp-nombre">Nombre del {contraparteLabel.toLowerCase()}</FieldLabel>
            <Input
              id="mc-cp-nombre"
              value={form.contraparteNombre}
              onChange={(e) => set('contraparteNombre', e.target.value)}
              placeholder="Razón social"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field>
              <FieldLabel htmlFor="mc-g10">Gravada 10%</FieldLabel>
              <Input id="mc-g10" inputMode="numeric" value={form.gravada10} onChange={(e) => set('gravada10', e.target.value)} placeholder="0" />
            </Field>
            <Field>
              <FieldLabel htmlFor="mc-g5">Gravada 5%</FieldLabel>
              <Input id="mc-g5" inputMode="numeric" value={form.gravada5} onChange={(e) => set('gravada5', e.target.value)} placeholder="0" />
            </Field>
            <Field>
              <FieldLabel htmlFor="mc-ex">Exentas</FieldLabel>
              <Input id="mc-ex" inputMode="numeric" value={form.exentas} onChange={(e) => set('exentas', e.target.value)} placeholder="0" />
            </Field>
            <Field>
              <FieldLabel htmlFor="mc-iva10">IVA 10%</FieldLabel>
              <Input id="mc-iva10" inputMode="numeric" value={form.iva10} onChange={(e) => set('iva10', e.target.value)} placeholder="0" />
            </Field>
            <Field>
              <FieldLabel htmlFor="mc-iva5">IVA 5%</FieldLabel>
              <Input id="mc-iva5" inputMode="numeric" value={form.iva5} onChange={(e) => set('iva5', e.target.value)} placeholder="0" />
            </Field>
          </div>

          <div className="flex items-center justify-end gap-4 rounded-lg border border-border bg-muted/30 px-4 py-2 text-sm">
            <span className="text-muted-foreground">
              IVA total <span className="font-medium tabular-nums text-foreground">{formatMoney(ivaTotal)}</span>
            </span>
            <span className="text-muted-foreground">
              Total <span className="font-bold tabular-nums text-foreground">{formatMoney(total)}</span>
            </span>
          </div>
        </FieldGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
